import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaFacebookF, FaInstagram, FaYoutube } from 'react-icons/fa';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 40);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isHome = location.pathname === '/';

    // Top Bar Styles
    const topBarStyle = {
        backgroundColor: '#111',
        color: 'var(--white)',
        padding: '10px 0',
        fontSize: '0.8rem',
        transition: 'all 0.4s ease',
        height: scrolled ? '0' : '40px',
        overflow: 'hidden',
        opacity: scrolled ? 0 : 1,
        display: mobileMenuOpen ? 'none' : 'block' // Hide on mobile menu open
    };

    const navStyle = {
        position: 'fixed',
        top: scrolled ? '0' : '40px', // Move up when top bar disappears
        left: 0,
        width: '100%',
        zIndex: 1000,
        backgroundColor: '#ffffff', // Solid white - no transparency
        boxShadow: scrolled ? '0 5px 30px rgba(0,0,0,0.05)' : '0 2px 10px rgba(0,0,0,0.03)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        padding: scrolled ? '15px 0' : '25px 0',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
    };

    // Adjust top position for mobile/responsive where top bar might be hidden via CSS
    // We'll handle responsive top bar hiding in CSS global styles if needed, 
    // but for now inline styles control the logic.

    const linkStyle = {
        color: 'var(--secondary)',
        fontWeight: '500',
        marginLeft: '40px',
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        position: 'relative',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)'
    };

    const logoStyle = {
        fontSize: '2rem',
        fontWeight: '700',
        color: 'var(--secondary)',
        fontFamily: 'var(--font-heading)',
        letterSpacing: '1px',
        zIndex: 1001,
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    };

    // Button styles - always dark on white navbar
    const btnBg = '#000000';
    const btnColor = '#ffffff';
    const btnBorder = 'none';

    const btnHoverBg = 'var(--primary)';
    const btnHoverColor = 'var(--white)';

    // Mobile Menu Styles
    const mobileMenuStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999,
        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.5s cubic-bezier(0.77, 0, 0.175, 1)',
        opacity: mobileMenuOpen ? 1 : 0,
        padding: '20px'
    };

    return (
        <>
            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }
                    
                    html, body {
                        overflow-x: hidden !important;
                        max-width: 100vw !important;
                    }

                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    /* Desktop: Show desktop menu, hide mobile toggle */
                    @media (min-width: 769px) {
                        .desktop-menu {
                            display: flex !important;
                        }
                        .mobile-toggle {
                            display: none !important;
                        }
                        .top-bar {
                            display: block !important;
                        }
                    }

                    /* Mobile: Hide desktop menu and top bar, show mobile toggle */
                    @media (max-width: 768px) {
                        body {
                            overflow-x: hidden !important;
                        }
                        
                        .desktop-menu {
                            display: none !important;
                        }
                        .mobile-toggle {
                            display: block !important;
                            padding: 10px;
                        }
                        .top-bar {
                            display: none !important;
                        }
                        nav {
                            top: 0 !important;
                            padding: 15px 0 !important;
                            left: 0 !important;
                            right: 0 !important;
                            width: 100% !important;
                            max-width: 100vw !important;
                            overflow-x: hidden !important;
                        }
                        nav .container {
                            padding: 0 20px !important;
                            max-width: 100% !important;
                            overflow-x: hidden !important;
                        }
                        nav img {
                            height: 50px !important;
                        }
                    }

                    /* Small mobile devices */
                    @media (max-width: 480px) {
                        nav img {
                            height: 45px !important;
                        }
                    }
                `}
            </style>

            {/* Top Bar - Desktop Only */}
            <div className="top-bar" style={topBarStyle}>
                <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaPhoneAlt style={{ color: 'var(--primary)', fontSize: '0.8rem' }} /> +91 70 70 70 55 90
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaEnvelope style={{ color: 'var(--primary)', fontSize: '0.9rem' }} /> info@nyssahealth.com
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <a href="https://www.facebook.com/profile.php?id=61584980792095" target="_blank" rel="noopener noreferrer">
                            <FaFacebookF style={{ cursor: 'pointer', color: '#fff' }} />
                        </a>
                        <a href="https://www.instagram.com/nyssa_health/?fbclid=IwY2xjawO8Z1VleHRuA2FlbQIxMQBicmlkETFJYzVqMnRJcmpMWmJWN2RDc3J0YwZhcHBfaWQBMAABHmB4x0SGawY4keoGNy4De6593RkHtyeeE2MJGtSXGIU1Tix-moVWfyJWcP4G_aem_bjkSpFruYMCZ66HHHQLd4Q" target="_blank" rel="noopener noreferrer">
                            <FaInstagram style={{ cursor: 'pointer', color: '#fff' }} />
                        </a>
                        {/* <a href="#" target="_blank" rel="noopener noreferrer">
                            <FaYoutube style={{ cursor: 'pointer', color: '#fff' }} />
                        </a> */}
                    </div>
                </div>
            </div>

            <nav style={navStyle}>
                <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Link to="/" style={{ ...logoStyle, margin: '-10px 0' }} onClick={() => setMobileMenuOpen(false)}>
                        <img src="/Logo.png" alt="NYSSA" style={{ height: '70px', width: 'auto' }} />
                    </Link>

                    {/* Desktop Menu */}
                    <div className="desktop-menu" style={{ display: 'flex', alignItems: 'center' }}>
                        {['Home', 'About', 'Services', 'Blogs', 'Contact'].map((item) => {
                            const path = item === 'Home' ? '/' : `/${item.toLowerCase()}`;
                            return (
                                <Link key={item} to={path} style={linkStyle}>
                                    <motion.div
                                        style={{ position: 'relative' }}
                                        whileHover="hover"
                                    >
                                        <span style={{ position: 'relative', zIndex: 1 }}>{item}</span>
                                        <motion.span
                                            variants={{
                                                hover: { width: '100%' }
                                            }}
                                            initial={{ width: '0%' }}
                                            style={{
                                                position: 'absolute',
                                                bottom: '-5px',
                                                left: 0,
                                                height: '1px',
                                                backgroundColor: 'var(--primary)',
                                                zIndex: 0
                                            }}
                                        />
                                    </motion.div>
                                </Link>
                            );
                        })}

                        <Link to="/#book-appointment" style={{ textDecoration: 'none' }}>
                            <motion.div
                                initial={{ backgroundColor: btnBg, color: btnColor, border: btnBorder }}
                                animate={{ backgroundColor: btnBg, color: btnColor, border: btnBorder }}
                                whileHover={{
                                    scale: 1.02,
                                    backgroundColor: btnHoverBg,
                                    color: btnHoverColor,
                                    border: '1px solid var(--primary)',
                                    boxShadow: '0 5px 15px rgba(181, 131, 131, 0.4)'
                                }}
                                whileTap={{ scale: 0.98 }}
                                className="btn"
                                style={{
                                    marginLeft: '40px',
                                    padding: '10px 28px',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px',
                                    borderRadius: '50px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '600',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Book Appointment
                            </motion.div>
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <div className="mobile-toggle" style={{ zIndex: 1001, cursor: 'pointer', display: 'none' }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        <div style={{
                            width: '28px',
                            height: '3px',
                            backgroundColor: 'var(--secondary)',
                            marginBottom: '5px',
                            transition: 'all 0.3s ease',
                            transform: mobileMenuOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none',
                            borderRadius: '2px'
                        }}></div>
                        <div style={{
                            width: '28px',
                            height: '3px',
                            backgroundColor: 'var(--secondary)',
                            marginBottom: '5px',
                            transition: 'all 0.3s ease',
                            opacity: mobileMenuOpen ? 0 : 1,
                            borderRadius: '2px'
                        }}></div>
                        <div style={{
                            width: '28px',
                            height: '3px',
                            backgroundColor: 'var(--secondary)',
                            transition: 'all 0.3s ease',
                            transform: mobileMenuOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none',
                            borderRadius: '2px'
                        }}></div>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                <div style={mobileMenuStyle}>
                    {['Home', 'About', 'Services', 'Blogs', 'Contact'].map((item, index) => {
                        const path = item === 'Home' ? '/' : `/${item.toLowerCase()}`;
                        return (
                            <Link key={item} to={path} onClick={() => setMobileMenuOpen(false)} style={{
                                fontSize: '1.8rem',
                                fontWeight: '400',
                                color: 'var(--secondary)',
                                margin: '12px 0',
                                fontFamily: 'var(--font-heading)',
                                textDecoration: 'none',
                                position: 'relative',
                                padding: '10px 20px',
                                transition: 'all 0.3s ease',
                                animation: mobileMenuOpen ? `slideIn 0.5s ease ${index * 0.1}s forwards` : 'none',
                                opacity: 0
                            }}>
                                {item}
                            </Link>
                        );
                    })}
                    <Link to="/#book-appointment" onClick={() => setMobileMenuOpen(false)} style={{ marginTop: '30px', animation: mobileMenuOpen ? 'slideIn 0.5s ease 0.5s forwards' : 'none', opacity: 0 }}>
                        <button style={{
                            backgroundColor: 'var(--primary)',
                            color: 'var(--white)',
                            padding: '16px 50px',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 4px 15px rgba(181, 131, 131, 0.3)',
                            transition: 'all 0.3s ease'
                        }}>
                            Book Appointment
                        </button>
                    </Link>

                    {/* Mobile Menu Contact Info */}
                    <div style={{
                        marginTop: '40px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '15px',
                        animation: mobileMenuOpen ? 'slideIn 0.5s ease 0.6s forwards' : 'none',
                        opacity: 0
                    }}>
                        <a href="tel:+917070705590" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            color: 'var(--secondary)',
                            textDecoration: 'none',
                            fontSize: '0.9rem'
                        }}>
                            <FaPhoneAlt style={{ color: 'var(--primary)' }} />
                            +91 70 70 70 55 90
                        </a>
                        <a href="mailto:info@nyssahealth.com" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            color: 'var(--secondary)',
                            textDecoration: 'none',
                            fontSize: '0.9rem'
                        }}>
                            <FaEnvelope style={{ color: 'var(--primary)' }} />
                            info@nyssahealth.com
                        </a>
                    </div>

                    {/* Mobile Menu Social Icons */}
                    <div style={{
                        marginTop: '25px',
                        display: 'flex',
                        gap: '20px',
                        animation: mobileMenuOpen ? 'slideIn 0.5s ease 0.7s forwards' : 'none',
                        opacity: 0
                    }}>
                        <a href="https://www.facebook.com/profile.php?id=61584980792095" target="_blank" rel="noopener noreferrer" style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}>
                            <FaFacebookF style={{ color: 'var(--white)', fontSize: '1.1rem' }} />
                        </a>
                        <a href="https://www.instagram.com/nyssa_health/?fbclid=IwY2xjawO8Z1VleHRuA2FlbQIxMQBicmlkETFJYzVqMnRJcmpMWmJWN2RDc3J0YwZhcHBfaWQBMAABHmB4x0SGawY4keoGNy4De6593RkHtyeeE2MJGtSXGIU1Tix-moVWfyJWcP4G_aem_bjkSpFruYMCZ66HHHQLd4Q" target="_blank" rel="noopener noreferrer" style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}>
                            <FaInstagram style={{ color: 'var(--white)', fontSize: '1.1rem' }} />
                        </a>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default Navbar;
